import * as React from "react";
import { DrawdownIncomeAmendmentModel } from "../../../../../../../Wrap/Models/Models/Processes/Drawdown/DrawdownIncomeAmendmentModel";
import { Navigator } from "../../../../../../Shared/Wizards/Entities/Navigator";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { ColumnType } from "../../../Common/Review/Component/CappedAndFlexiTableReview";
import { boolParse, dateParser, prepareTypography } from "../../../Common/Review/ReviewParser";
import { Declarations } from "./Declarations";
import { Urls } from "../../../../../Urls";
import { DrawdownAnnualIncome } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualIncome";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { FrequencyArraysMap } from "../../../Common/Resource/Contents";
import { editIncomePaymentDetailsLabels } from "../../Resource/Contents";
import { BankDetails } from "../../../GadReviewWizard/Components/Review/IncomePaymentDetails/BankDetails";
import { BankAccount } from "../../../../../../../Wrap/Models/Domains/BankAccount/BankAccount";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { AssetsToSellTable } from "./AssetsToSellTable";

interface IReviewProps extends IProcessComponentProps {
    data: DrawdownIncomeAmendmentModel;
    extraInfo: {
        AllBankAccounts: Array<BankAccount>;
        Drawdowntype: DrawdownType;
    };
}

interface IReviewState {}

const columnsTypes = {
    CappedcolumnsType: [
        { Title: "From", Key: "From", type: ColumnType.PlainText },
        { Title: "Taxable", Key: "Taxable", type: ColumnType.bool },
        { Title: "GAD Maximum", Key: "GADMaximum", type: ColumnType.Money },
        { Title: "Income taken to date", Key: "Incometakentodate", type: ColumnType.Money },
        { Title: "Last mandatory review", Key: "Lastmandatoryreview", type: ColumnType.PlainText },
        {
            Title: "Maximum Income Payment For Remainder Of Pension Year",
            Key: "MaximumIncomePaymentForRemainderOfPensionYear",
            type: ColumnType.Money,
        },
        {
            Title: "Maximum Income Payment For Future Pension Years",
            Key: "MaximumIncomePaymentForFuturePensionYears",
            type: ColumnType.Money,
        },
        {
            Title: "Income Payment For Remainder Of Pension Year",
            Key: "IncomePerPaymentForCurrentPensionYear",
            type: ColumnType.Money,
        },
        {
            Title: "Income Payment For Future Pension Years",
            Key: "IncomePerPaymentForFuturePensionYears",
            type: ColumnType.Money,
        },
    ],
    FlexicolumnsType: [
        { Title: "From", Key: "From", type: ColumnType.PlainText },
        { Title: "Type", Key: "Type", type: ColumnType.PlainText },
        { Title: "Maximum Income Per Payment", Key: "MaximumIncomePerPayment", type: ColumnType.Money },
        {
            Title: "Income Payment",
            Key: "IncomePerPayment",
            type: ColumnType.Money,
        },
    ],
};

const incomePaymentConfig = (annualIncome: DrawdownAnnualIncome) => {
    const incomePaymentConfig = [
        {
            title: editIncomePaymentDetailsLabels.targetSpecificArrangements,
            data: boolParse(annualIncome?.TargetSpecificArrangements),
        },
    ];
    if (annualIncome?.TargetSpecificArrangements === false) {
        incomePaymentConfig.push({
            title: editIncomePaymentDetailsLabels.takeDifferentInitialIncomePayment,
            data: boolParse(annualIncome?.TakeDifferentInitialIncomePayment),
        });
        if (annualIncome?.TakeDifferentInitialIncomePayment) {
            incomePaymentConfig.push({
                title: editIncomePaymentDetailsLabels.grossInitialIncomePayment,
                data: MoneyDisplayHelper.getMoneyFormat(annualIncome?.GrossInitialIncomePayment),
            });
        }

        incomePaymentConfig.push({
            title: editIncomePaymentDetailsLabels.grossIncomePerPayment,
            data: MoneyDisplayHelper.getMoneyFormat(annualIncome?.GrossIncomePerPayment),
        });
    } else {
        incomePaymentConfig.push({
            title: editIncomePaymentDetailsLabels.grossIncomePerPaymentFromPension,
            data: MoneyDisplayHelper.getMoneyFormat(annualIncome?.GrossIncomePerPaymentFromPension),
        });
    }

    incomePaymentConfig.push(
        {
            title: editIncomePaymentDetailsLabels.incomeFrequency,
            data: FrequencyArraysMap[annualIncome?.Frequency],
        },
        {
            title: editIncomePaymentDetailsLabels.firstPaymentDate,
            data: annualIncome?.NextPayment ? dateParser(annualIncome?.NextPayment) : null,
        },
        {
            title: "End date",
            data: annualIncome?.PaymentEnd == null ? null : dateParser(annualIncome?.PaymentEnd),
        }
    );

    return incomePaymentConfig;
};

export class Review extends ProcessComponent<IReviewProps, IReviewState> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    public render(): JSX.Element {
        const { data, extraInfo, showErrorMsgPopUp } = this.props;
        const formattedIncome = data?.DrawdownIncomeAmendmentInstruction?.Income?.TargetSpecificArrangements
            ? MoneyDisplayHelper.FromatMoneyDisplay(
                  data?.DrawdownIncomeAmendmentInstruction?.Income?.GrossIncomePerPaymentFromPension?.Amount,
                  CurrencyCode.GBP,
                  true,
                  2
              )
            : MoneyDisplayHelper.FromatMoneyDisplay(
                  data?.DrawdownIncomeAmendmentInstruction?.Income?.GrossIncomePerPayment?.Amount,
                  CurrencyCode.GBP,
                  true,
                  2
              );
        return (
            <>
                <h2>Review</h2>
                <div className="row narrow-gutter">
                    <h2 className="col-fluid heading-underlined mt-20">Income payment details</h2>
                    <div className="col-auto">
                        <button
                            className="edit-circle-icon-button label-sr-only"
                            title="Edit"
                            onClick={() => {
                                Navigator.navigateToAndPreserveQueryParameters(
                                    Urls.get().EditIncomeWithdrawalsIncomePaymenDetails
                                );
                            }}
                        >
                            <span className="icon" aria-hidden="true"></span>
                            <span className="text">Edit</span>
                        </button>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 col-sm-fluid">
                        <h3>Before</h3>
                        <Typography
                            {...prepareTypography(incomePaymentConfig(data?.DrawdownIncome?.AnnualIncome), 1)}
                        ></Typography>
                    </div>
                    <div className="col-12 col-sm-fluid">
                        <h3>After</h3>
                        <Typography
                            {...prepareTypography(
                                incomePaymentConfig(data?.DrawdownIncomeAmendmentInstruction.Income),
                                1
                            )}
                        ></Typography>
                    </div>
                </div>
                {data?.DrawdownIncomeAmendmentInstruction?.IncomeDisinvestmentAllocations?.length ? (
                    <AssetsToSellTable
                        existingData={data.DrawdownIncome.IncomeDisinvestmentAllocations || []}
                        tableData={data.DrawdownIncomeAmendmentInstruction.IncomeDisinvestmentAllocations || []}
                        title={`Choose assets to sell to pay income (${formattedIncome})`}
                    />
                ) : (
                    <div>
                        <dt>
                            <div className="row-auto">
                                <h4>Asset selection method</h4>
                            </div>
                        </dt>
                        <dd>Proportionally across all assets</dd>
                    </div>
                )}
                <BankDetails data={data} extraInfo={extraInfo?.AllBankAccounts} />
                <Declarations
                    data={data}
                    onChange={() => {}}
                    onRef={(ref) => this.appendChild(ref)}
                    showErrorMsgPopUp={(msg, isShow) => showErrorMsgPopUp(msg, isShow)}
                ></Declarations>
            </>
        );
    }
}
